import React, { useState } from 'react';
import { Typography, Box, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import useAlert from '../../alert/useAlert.js';
import CreateSubscription from '../Subscription/CreateSubscription.js';
import PricingPlans from '../../components/PricingPlans.js';
import { SUBSCRIPTION_PLANS } from '../../constants/subscriptionPlans.js';
import { activateFreePlan } from '../../utils/freePlanActivation.js';
import OnboardingLayout from './OnboardingLayout.js';

const Step7PlanOverview = ({ ctx, onFinish, onStepClick }) => {
  const { email, initials, displayName } = ctx;
  const { setAlert } = useAlert();
  const [activating, setActivating] = useState(false);
  const [checkout, setCheckout] = useState(null);
  // activate_free_plan() is the sole authority on free-plan eligibility: it
  // checks the one-time trial key, writes subscription_info and grants the
  // trial credits in one transaction. No client-side pre-existence guard and
  // no client-side write — a stale bundle must fail closed, not race the RPC.
  // Interpretation of the answer lives in utils/freePlanActivation.js so this
  // step, the checkout form and the Settings plan picker cannot drift apart.
  const handleContinueFree = async () => {
    setActivating(true);
    try {
      const outcome = await activateFreePlan(SUBSCRIPTION_PLANS.FREE);
      if (!outcome.settled) {
        console.error('Free plan activation failed:', outcome.reason, outcome.message);
        setAlert('Unable to activate the Free plan. Please try again.', 'error');
        return;
      }
      // already_granted / paid_plan_active still mean provisioned: finish the
      // wizard rather than blocking onboarding on a call that cannot succeed.
      // The user still hears which of the three happened — finishing silently
      // would let someone whose trial was already granted leave onboarding
      // believing they just received credits.
      setAlert(outcome.message, outcome.severity);
      await onFinish('/dashboard');
    } finally {
      setActivating(false);
    }
  };

  return (
    <OnboardingLayout step={7} title="Find the right plan for your practice"
      description="Start with a free month. Use your credits across the tools you need, and change plans as your practice grows."
      sidebarExtra={<Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #dbe9eb' }}>
        <Typography fontWeight={700}>Simple, flexible credits</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Each activity uses credits from one shared balance. Recharge whenever you need more.
        </Typography>
      </Box>}
      avatarInitials={initials} onStepClick={onStepClick}>
      <PricingPlans showFreePlan busy={activating} onPlanSelected={(plan, cadence) => {
        if (plan.tier === 'free') handleContinueFree();
        else setCheckout({ plan, cadence });
      }} />
      <Dialog open={!!checkout} onClose={() => setCheckout(null)} fullWidth maxWidth="md">
        <DialogTitle>Complete your {checkout?.plan.name} subscription</DialogTitle>
        <DialogContent>
          {checkout && <CreateSubscription key={`${checkout.plan.tier}-${checkout.cadence}`}
            name={displayName} email={email} initialPlan={checkout.plan.name}
            initialCadence={checkout.cadence} checkoutOnly
            onSubscriptionComplete={() => onFinish('/dashboard')} />}
        </DialogContent>
        <DialogActions><Button onClick={() => setCheckout(null)}>Close</Button></DialogActions>
      </Dialog>
    </OnboardingLayout>
  );
};
export default Step7PlanOverview;
